import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import comprobanteService from "../../services/comprobanteService";
import * as serieService from "../../services/serieService";
import { setData } from "../../redux/receiptSlice";
import { Grid, Card, CardContent, Snackbar, Alert, TextField, Button } from '@mui/material';
import { formatoMonto } from '../../components/common/MontoTextField';
import { Print as IconPrint } from "@mui/icons-material";
import Comprobante from '../../components/Receipt'
import ReceiptDetail from '../../components/ReceiptDetail'
import ReceiptPayment from '../../components/ReceiptPayment'
import MantenimientoForm from '../../components/common/MantenimientoForm';
import { useMantenimiento } from '../../hooks/useMantenimiento';
import { activeValidations as activeValidationsComprobante, setEntity } from '../../redux/receiptSlice';

// Catalogo 143 = Estados de Pago del comprobante.
export const ESTADO_PAGO_PENDIENTE = 144;
export const ESTADO_PAGO_CANCELADO = 145;

const Edit = (props) => {
   const params = useParams();
   const navigate = useNavigate();
   const dispatch = useDispatch();

   // El mantenimiento del comprobante se puede abrir como pagina (con :id en la
   // ruta) o embebido en un dialogo, en cuyo caso el identificador llega por prop.
   // Cuando va en dialogo, la pantalla que lo abrio recibe el identificador
   // guardado en vez de navegar a el.
   const idComprobante = props.idComprobante != null ? props.idComprobante : params.id;
   const alGuardar = props.onSaved;

   const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
   const receipt = useSelector((state) => state.receipt["Normal"]?.entity);
   const listEstadoPago = useSelector((state) => state.receipt["Normal"]?.listEstadoPago);
   const receiptValidation = useSelector((state) => state.receipt["Normal"]?.validationMessage);
   const [openMessage, setOpenMessage] = useState(false)
   const onCloseMessage = () => { setOpenMessage(!openMessage); }
   const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });
   const [receiptDetails, setReceiptDetails] = useState([]);
   const [receiptPayments, setReceiptPayments] = useState([]);
   const { isReadOnly, setIsReadOnly } = useMantenimiento("comprobante");

  // Soles (catalogo 137) no se convierten. El tipo de cambio pasa un monto a
  // soles. El total y el saldo quedan en la moneda del comprobante.
  const MONEDA_SOLES = 137;
  const tipoCambioASoles = (idMoneda, tipoCambio) => {
    if (parseInt(idMoneda, 10) === MONEDA_SOLES) return 1;
    const cambio = parseFloat(tipoCambio);
    return cambio > 0 ? cambio : 1;
  };
  const redondearMonto = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

  // El sub total de cada item es cantidad por precio unitario. El total suma
  // esos sub totales. El saldo descuenta los pagos, llevados a la misma moneda
  // del comprobante, y el estado de pago se deduce de ese saldo.
  useEffect(() => {
    const total = redondearMonto((receiptDetails || []).reduce((sum, item) => sum + (parseFloat(item.subTotal) || 0), 0));
    const cambioDocumento = tipoCambioASoles(receipt?.idMoneda, receipt?.tipoCambio);
    const pagado = redondearMonto((receiptPayments || []).reduce((sum, item) => {
      const cambioPago = tipoCambioASoles(item.idMoneda ?? receipt?.idMoneda, item.tipoCambio ?? receipt?.tipoCambio);
      return sum + ((parseFloat(item.monto) || 0) * cambioPago) / cambioDocumento;
    }, 0));
    const saldo = Math.max(redondearMonto(total - pagado), 0);
    const todosConsolidados = (receiptPayments || []).length > 0 && (receiptPayments || []).every((p) => Number(p.idEstado) === 227);
    const estadoPago = saldo <= 0 && total > 0 ? (todosConsolidados ? 227 : ESTADO_PAGO_CANCELADO) : ESTADO_PAGO_PENDIENTE;

    dispatch(setData({sliceKey: "Normal", name: "montoTotal", value: total}));
    dispatch(setData({sliceKey: "Normal", name: "montoSaldo", value: saldo}));
    dispatch(setData({sliceKey: "Normal", name: "idEstadoPago", value: estadoPago}));
    if (idComprobante == 0) {
      dispatch(setData({sliceKey: "Normal", name: "saldoDisponible", value: estadoPago === ESTADO_PAGO_CANCELADO ? total : 0}));
    }
  }, [receiptDetails, receiptPayments, receipt?.idMoneda, receipt?.tipoCambio, idComprobante]);

  // El comprobante ya guardado no se edita desde aqui si ya tiene pagos, esta validado (consolidado) o anulado.
  const esComprobanteNuevo = idComprobante == 0;
  const comprobanteConPagos = !esComprobanteNuevo && (receiptPayments || []).length > 0;
  const comprobanteAnuladoOValidado = !esComprobanteNuevo
    && (Number(receipt?.idEstado) === 4 || Number(receipt?.idEstadoPago) === 227);
  const soloLecturaComprobante = isReadOnly || comprobanteConPagos || comprobanteAnuladoOValidado;

  // Cuando se abre el comprobante desde el pago de periodo de terapia (detalleSoloLectura),
  // el detalle es de solo lectura: no se muestran los campos de entrada ni las opciones
  // editar o quitar.
  const soloLecturaDetalle = soloLecturaComprobante || Boolean(props.detalleSoloLectura);

  const pagoAnulado = async () => {
    if (idComprobante > 0) {
      const response = await comprobanteService.getComprobanteView(idComprobante).catch(() => {});
      if (response?.data?.data) {
        dispatch(setEntity({ sliceKey: "Normal", data: response.data.data }));
      }
    }
  };

  // El estado de pago se muestra en solo lectura y se deduce del saldo.
  const ESTADO_PAGO_DESCRIPCION = {
    [ESTADO_PAGO_PENDIENTE]: "Pendiente",
    [ESTADO_PAGO_CANCELADO]: "Cancelado",
    227: "Pago Consolidado",
    146: "Anulado",
  };
  const estadoPagoDescripcion = (listEstadoPago || [])
    .find((row) => row.id == receipt?.idEstadoPago)?.descripcion
    || ESTADO_PAGO_DESCRIPCION[receipt?.idEstadoPago]
    || "";

  const postEntity = async () => {
    var dataPost = {
      ...receipt,
      comprobanteDetalle: receiptDetails,
      comprobantePago: receiptPayments
    }

    // Si el usuario dejo el numero vacio se genera el siguiente de la serie.
    if (idComprobante == 0 && (dataPost.numero == null || dataPost.numero === "" || dataPost.numero === 0)) {
      const series = await serieService.getsSerie()
        .catch((err) => { console.log("Err: ", err) });

      const serie = series?.find((row) => row.codigo === dataPost.serie);

      if (serie) {
        dataPost.numero = await serieService.obtenerSiguienteNumero(serie.id)
          .catch((err) => { console.log("Err: ", err) });
      }
    }

    if (dataPost.numero != null && dataPost.numero !== "") {
      dataPost.numero = parseInt(dataPost.numero);
    }

    var ValidationMessages = receiptValidation || []

    if (ValidationMessages.length > 0) {
      dispatch(activeValidationsComprobante({sliceKey: "Normal"}));

      setPropsMessage({ severity: "error", message: ValidationMessages.join(" - ") })
      setOpenMessage(true);
    }
    else {
      const totalComprobante = parseFloat(dataPost.montoTotal) || 0;
      const cambioDocumento = tipoCambioASoles(receipt?.idMoneda, receipt?.tipoCambio);
      const pagado = redondearMonto((receiptPayments || []).reduce((sum, item) => {
        if (Number(item.idEstado) === 4 || Number(item.idEstado) === 146 || item.estado === "Anulado") return sum;
        const cambioPago = tipoCambioASoles(item.idMoneda ?? receipt?.idMoneda, item.tipoCambio ?? receipt?.tipoCambio);
        return sum + ((parseFloat(item.monto) || 0) * cambioPago) / cambioDocumento;
      }, 0));

      if (totalComprobante > 0 && pagado > redondearMonto(totalComprobante + 0.009)) {
        setPropsMessage({ severity: "error", message: `El total pagado (${formatoMonto(pagado)}) no puede ser mayor al total del comprobante (${formatoMonto(totalComprobante)})` });
        setOpenMessage(true);
        return;
      }

      if (totalComprobante <= 0 && pagado > 0) {
        setPropsMessage({ severity: "error", message: "No se pueden registrar pagos en un comprobante con monto total cero." });
        setOpenMessage(true);
        return;
      }

      dataPost.idFilial = dataPost.idFilial || receipt?.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 1);
      await comprobanteService.addUpdateComprobanteWithDetails(dataPost)
        .then(response => {
          const idGuardado = parseInt(response.data.data);

          // Se refleja en la pantalla lo que quedo guardado: el numero generado
          // y los montos, y se navega al comprobante ya registrado.
          dispatch(setData({sliceKey: "Normal", name: "numero", value: dataPost.numero}));
          dispatch(setData({sliceKey: "Normal", name: "montoTotal", value: dataPost.montoTotal}));
          dispatch(setData({sliceKey: "Normal", name: "montoSaldo", value: dataPost.montoSaldo}));
          dispatch(setData({
            sliceKey: "Normal",
            name: "saldoDisponible",
            value: dataPost.idEstadoPago == ESTADO_PAGO_CANCELADO ? dataPost.montoTotal : 0
          }));

          setPropsMessage({ severity: "success", message: "Se guardaron los datos con exito" })
          setOpenMessage(true);
          setIsReadOnly(true);

          if (idComprobante == 0 && idGuardado > 0) {
            // En el dialogo se avisa el identificador guardado para que la pantalla
            // que lo abrio lo referencie; como pagina se navega a el.
            if (alGuardar) {
              alGuardar(idGuardado);
            }
            else {
              navigate("/receipt/edit/" + idGuardado);
            }
          }
        })
        .catch(error => {
          const msg = error?.response?.data?.detail || error?.response?.data?.Detail || "Hubo un error al guardar el comprobante";
          setPropsMessage({ severity: "error", message: msg })
          setOpenMessage(true);
        });
    }
  }

  const valorSaldoDisponible = (receipt?.idEstadoPago == ESTADO_PAGO_CANCELADO || receipt?.idEstadoPago == 227)
    ? formatoMonto(esComprobanteNuevo ? (receipt?.montoTotal || 0) : (receipt?.saldoDisponible || 0))
    : formatoMonto(0);

  return (
    <div>
      <MantenimientoForm
        title={(idComprobante == 0 ? "Nuevo" : "Editar") + " Comprobante"}
        backTo={props.enDialogo ? null : "/receipt"}
        onSave={postEntity}
        disabled={soloLecturaComprobante}
        extraActions={
          <Button
            variant="outlined"
            size="small"
            startIcon={<IconPrint />}
            onClick={() => navigate("/receipt/print/" + idComprobante)}
            disabled={idComprobante == 0}
          >
            Imprimir
          </Button>
        }
      >
      <Grid container spacing={1} sx={{
        "& .MuiGrid-root>.MuiGrid-item": {
          paddingTop: "4px"
        },
      }}>
        <Grid item xs={12} sm={12}>
          <Card>
            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
              <Comprobante sliceKey={"Normal"} isReadOnly={soloLecturaComprobante} idComprobante={idComprobante} prefill={props.prefill} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Card>
            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
              <ReceiptDetail sliceKey={"Normal"} setDetails={setReceiptDetails} esNuevo={esComprobanteNuevo} isReadOnly={soloLecturaDetalle} idComprobante={idComprobante} initialDetails={props.initialDetails} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Card>
            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
              <ReceiptPayment sliceKey={"Normal"} setPayments={setReceiptPayments} esNuevo={esComprobanteNuevo} permitirAgregar={esComprobanteNuevo} isReadOnly={isReadOnly || (Number(receipt?.idEstado) === 4)} idComprobante={idComprobante} onPaymentAnnulled={pagoAnulado} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Card>
            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
              <Grid container spacing={1} alignItems="center" sx={{ paddingTop: "4px" }}>
                <Grid item xs={12} sm={2}>
                  <TextField
                    name="observaciones"
                    label="Observaciones"
                    fullWidth
                    value={receipt?.observaciones || ""}
                    onChange={(event) => {
                      dispatch(setData({sliceKey: "Normal", name: "observaciones", value: event.target.value}))
                    }}
                    disabled={soloLecturaComprobante}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    disabled
                    name="montoTotal"
                    label="Total"
                    fullWidth
                    value={formatoMonto(receipt?.montoTotal || 0)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    disabled
                    name="totalPago"
                    label="Total Pago"
                    fullWidth
                    value={formatoMonto((receiptPayments || []).reduce((sum, item) => sum + (parseFloat(item.monto) || 0), 0))}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    disabled
                    name="montoSaldo"
                    label="Saldo"
                    fullWidth
                    value={formatoMonto(receipt?.montoSaldo || 0)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    disabled
                    name="saldoDisponible"
                    label="Saldo Disponible"
                    fullWidth
                    value={valorSaldoDisponible}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    disabled
                    name="estadoPago"
                    label="Estado de Pago"
                    fullWidth
                    value={estadoPagoDescripcion}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </MantenimientoForm>
      <Snackbar open={openMessage} autoHideDuration={5000} onClose={onCloseMessage}
        anchorOrigin={{vertical: "top", horizontal: "center"}}>
        <Alert onClose={onCloseMessage} severity={propsMessage.severity}>
          {propsMessage.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default Edit
